import { HttpResponseType } from 'bitmovin-player';
import { npoCdnProviders } from '../../../services/cdnProviders/cdnProviders';
import { replaceSpecialCharacters } from '../../utilities/utilities.text';
async function setDrm(sourceConfig, drm, _streamObject, useWidevineServerCertificate) {
    const npoDrmGateway = 'https://npo-drm-gateway.samgcloud.nepworldwide.nl/authentication?custom_data=';
    switch (drm) {
        case 'fairplay': {
            sourceConfig.drm = {
                fairplay: {
                    certificateURL: 'https://fairplay.npo.nl/certificate/fairplay.cer',
                    LA_URL: npoDrmGateway + _streamObject.stream.drmToken,
                    prepareMessage: (event) => {
                        return new Uint8Array(event.message);
                    },
                    prepareContentId: (url) => {
                        const link = document.createElement('a');
                        const trimmedUrl = [...url].filter((char) => !(char.codePointAt(0) == undefined)).join('');
                        link.href = trimmedUrl.replace('T', '');
                        return link.hostname;
                    },
                    prepareLicense: (license) => {
                        let binary = '';
                        const bytes = new Uint8Array(license);
                        const len = bytes.byteLength;
                        for (let i = 0; i < len; i++) {
                            binary += String.fromCodePoint(bytes[i]);
                        }
                        return window.btoa(binary);
                    },
                    licenseResponseType: HttpResponseType.ARRAYBUFFER,
                    useUint16InitData: true
                }
            };
            break;
        }
        case 'widevine': {
            try {
                sourceConfig.drm = {
                    widevine: {
                        LA_URL: npoDrmGateway + _streamObject.stream.drmToken,
                        audioRobustness: 'SW_SECURE_CRYPTO',
                        videoRobustness: 'SW_SECURE_CRYPTO',
                        serverCertificate: useWidevineServerCertificate
                            ? await getServerCertificate('https://widevine.npoplayer.nl/widevine_player_cert.bin')
                            : undefined
                    }
                };
            }
            catch (error) {
                console.error('Error:', error);
            }
            break;
        }
        case 'playready': {
            sourceConfig.drm = {
                playready: {
                    LA_URL: npoDrmGateway + _streamObject.stream.drmToken
                }
            };
            break;
        }
    }
    return sourceConfig;
}
async function getServerCertificate(url) {
    const response = await fetch(url);
    return response.arrayBuffer();
}
const getSubtitleLabels = function (data) {
    if (data.label === 'nl') {
        return 'Nederlands';
    }
    return data.label;
};
const getQualityLabels = function (data) {
    return data.height + 'p';
};
function setStreamProfile(sourceConfig, _sourceConfig, _streamObject) {
    const streamProfile = _streamObject.stream.streamProfile;
    const streamURL = _streamObject.stream.streamURL;
    const configMapping = {
        hls: _sourceConfig.hls,
        dash: _sourceConfig.dash,
        progressive: _sourceConfig.progressive
    };
    if (Object.prototype.hasOwnProperty.call(configMapping, streamProfile)) {
        const value = configMapping[streamProfile];
        if (typeof value === 'string' || value === undefined) {
            sourceConfig[streamProfile] = value === '' ? undefined : value ?? streamURL ?? '';
        }
        else if (Array.isArray(value) && streamProfile === 'progressive') {
            sourceConfig.progressive = value;
        }
        for (const profile of Object.keys(configMapping).filter((profile) => profile !== streamProfile)) {
            if (profile === 'progressive') {
                sourceConfig[profile] = undefined;
            }
            else {
                sourceConfig[profile] = '';
            }
        }
    }
    return sourceConfig;
}
export async function processSourceConfig(npoPlayerServices, source, _sourceConfig = {}, _streamObject, drm = undefined, streamOptions, version, npoTagInstance) {
    let sourceConfig = {};
    sourceConfig.title = _sourceConfig.title
        ? replacePlaceholders(_sourceConfig.title, _streamObject.metadata)
        : _streamObject.metadata.title ?? '';
    sourceConfig.description = _sourceConfig.description
        ? replacePlaceholders(_sourceConfig.description, _streamObject.metadata)
        : _streamObject.metadata.description ?? '';
    sourceConfig.poster = decidePoster(_streamObject, _sourceConfig, streamOptions);
    sourceConfig.metadata = {
        title: sourceConfig.title,
        description: sourceConfig.description,
        poster: sourceConfig.poster
    };
    if (streamOptions.fragments || _streamObject.segment) {
        const segment = streamOptions.fragments
            ? npoPlayerServices.convertFragmentToSegment(streamOptions.fragments)
            : _streamObject.segment;
        if (segment) {
            ;
            sourceConfig.markers = npoPlayerServices.segmentMarkersHandler(segment, sourceConfig.metadata.title);
        }
    }
    sourceConfig.subtitleTracks =
        _sourceConfig.subtitleTracks ??
            (_streamObject.assets.subtitles
                ? _streamObject.assets.subtitles.map((subtitle, i) => ({
                    url: subtitle.location,
                    lang: subtitle.iso,
                    label: subtitle.name,
                    kind: 'subtitle',
                    id: 'sub' + i
                }))
                : undefined);
    sourceConfig.thumbnailTrack =
        _sourceConfig.thumbnailTrack ?? _streamObject.assets.scrubbingThumbnail
            ? { url: _streamObject.assets.scrubbingThumbnail }
            : undefined;
    sourceConfig = setStreamProfile(sourceConfig, _sourceConfig, _streamObject);
    sourceConfig = await setDrm(sourceConfig, drm, _streamObject, streamOptions.useWidevineServerCertificate ?? true);
    const streamSource = _streamObject.stream.streamURL;
    let cdnString = streamSource;
    if (streamSource.startsWith('file:///')) {
        cdnString = 'OFFLINE';
    }
    else {
        for (const [key, value] of npoCdnProviders.entries()) {
            if (streamSource.includes(key)) {
                cdnString = value;
            }
        }
    }
    if (sourceConfig.analytics == undefined)
        sourceConfig.analytics = {};
    sourceConfig.analytics.cdnProvider = cdnString;
    sourceConfig.analytics.videoId = _streamObject.metadata.prid;
    sourceConfig.analytics.title = _streamObject.metadata.title;
    sourceConfig.analytics.customData2 = _streamObject.user.type;
    sourceConfig.analytics.customData3 = npoPlayerServices.getAVType(_streamObject.stream.avType);
    sourceConfig.analytics.customData4 = npoTagInstance?.getParty();
    sourceConfig.analytics.customData5 = version;
    sourceConfig.analytics = {
        ...sourceConfig.analytics,
        ..._sourceConfig.analytics
    };
    if (sourceConfig.progressive !== '') {
        sourceConfig.labeling = {
            dash: {
                qualities: getQualityLabels,
                subtitles: getSubtitleLabels
            },
            hls: {
                qualities: getQualityLabels,
                subtitles: getSubtitleLabels
            }
        };
    }
    sourceConfig.metadata = {
        ...sourceConfig.metadata
    };
    sourceConfig.metadata.jwt = source;
    sourceConfig.metadata.streamLinkAsJsonString = JSON.stringify(_streamObject);
    sourceConfig.metadata.npoTagSession = String(npoTagInstance?.getSerializedSessionInfo());
    sourceConfig.metadata.hasPreroll = _streamObject.metadata.hasPreroll || 'false';
    sourceConfig.metadata.prerollUrl = _streamObject.assets.preroll || '';
    sourceConfig.metadata.onScreenDebug = 'false';
    sourceConfig.metadata.frameworkLevelDebug = 'false';
    sourceConfig.metadata.description = sourceConfig.description || '';
    sourceConfig.metadata.avType = npoPlayerServices.getAVType(_streamObject.stream.avType);
    return sourceConfig;
}
function decidePoster(streamObject, sourceConfig, streamOptions) {
    if (sourceConfig.poster) {
        return replaceSpecialCharacters(sourceConfig.poster);
    }
    if (streamObject.metadata.poster && !streamObject.metadata.posterIsDefault) {
        return streamObject.metadata.poster;
    }
    if (streamObject.metadata.posterIsDefault && streamOptions.customFallbackPoster) {
        return replaceSpecialCharacters(streamOptions.customFallbackPoster);
    }
    return streamObject.metadata.poster ?? '';
}
function replacePlaceholders(template, metadata) {
    return template.replace(/{([^{}]*)}/g, (match, key) => {
        const value = metadata[key];
        if (Array.isArray(value)) {
            return value.map((item) => item.trim()).join(', ');
        }
        return value === undefined ? '' : String(value);
    });
}
